import { Link, useLocation } from 'react-router-dom'
import {
  LayoutDashboard, ClipboardList, Target, MessageSquare, BookOpen,
  Columns3, Clock, GitPullRequest, BarChart2, Bookmark,
  RotateCcw, Flag, Users, FileText, DollarSign, Settings, Ship,
  Lock,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { canManage } from '@/lib/constants'
import type { Phase } from '@/lib/constants'
import { isFeatureAvailable, getUnlockPhase } from '@/lib/phase-features'
import { PHASE_LABELS } from '@/lib/constants'

interface ProjectTabsProps {
  projectId: string
  userRole: string
  phase?: Phase
}

interface TabItem {
  label: string
  href: string
  key: string
  icon: React.ReactNode
  group: string
  requiresManage?: boolean
}

export function ProjectTabs({ projectId, userRole, phase }: ProjectTabsProps) {
  const { pathname } = useLocation()
  const base = `/hub/projects/${projectId}`

  const tabs: TabItem[] = [
    { label: 'Overview', href: base, key: 'overview', icon: <LayoutDashboard className="h-4 w-4" />, group: 'core' },
    { label: 'Surveys', href: `${base}/surveys`, key: 'surveys', icon: <ClipboardList className="h-4 w-4" />, group: 'core' },
    { label: 'Milestones', href: `${base}/milestones`, key: 'milestones', icon: <Target className="h-4 w-4" />, group: 'core' },
    { label: 'Discussions', href: `${base}/discussions`, key: 'discussions', icon: <MessageSquare className="h-4 w-4" />, group: 'collaborate' },
    { label: 'Wiki', href: `${base}/wiki`, key: 'wiki', icon: <BookOpen className="h-4 w-4" />, group: 'collaborate' },
    { label: 'Kanban', href: `${base}/kanban`, key: 'kanban', icon: <Columns3 className="h-4 w-4" />, group: 'collaborate' },
    { label: 'Standups', href: `${base}/standups`, key: 'standups', icon: <Clock className="h-4 w-4" />, group: 'collaborate' },
    { label: 'Time', href: `${base}/time-tracking`, key: 'time-tracking', icon: <Clock className="h-4 w-4" />, group: 'collaborate' },
    { label: 'Reviews', href: `${base}/reviews`, key: 'reviews', icon: <GitPullRequest className="h-4 w-4" />, group: 'collaborate' },
    { label: 'Polls', href: `${base}/polls`, key: 'polls', icon: <BarChart2 className="h-4 w-4" />, group: 'collaborate' },
    { label: 'Bookmarks', href: `${base}/bookmarks`, key: 'bookmarks', icon: <Bookmark className="h-4 w-4" />, group: 'collaborate' },
    { label: 'Retros', href: `${base}/retros`, key: 'retros', icon: <RotateCcw className="h-4 w-4" />, group: 'collaborate' },
    { label: 'OKRs', href: `${base}/okrs`, key: 'okrs', icon: <Flag className="h-4 w-4" />, group: 'collaborate' },
    { label: 'Files', href: `${base}/files`, key: 'files', icon: <FileText className="h-4 w-4" />, group: 'core' },
    { label: "Captain's Bridge", href: `${base}/captain`, key: 'captain', icon: <Ship className="h-4 w-4" />, group: 'command', requiresManage: true },
    { label: 'Crew Deck', href: `${base}/crew-deck`, key: 'crew-deck', icon: <Users className="h-4 w-4" />, group: 'command', requiresManage: true },
    { label: 'Finances', href: `${base}/finances`, key: 'finances', icon: <DollarSign className="h-4 w-4" />, group: 'command', requiresManage: true },
    { label: 'Settings', href: `${base}/settings`, key: 'settings', icon: <Settings className="h-4 w-4" />, group: 'command', requiresManage: true },
  ]

  const visibleTabs = tabs.filter(tab => !tab.requiresManage || canManage(userRole))

  function isActive(href: string) {
    if (href === base) return pathname === base
    return pathname.startsWith(href)
  }

  return (
    <div className="border-b border-border">
      <nav className="flex items-center gap-0.5 overflow-x-auto px-1 scrollbar-none">
        {visibleTabs.map((tab) => {
          const available = tab.key === 'overview' || !phase || isFeatureAvailable(tab.key, phase)

          if (!available) {
            const unlockPhase = getUnlockPhase(tab.key)
            const tooltip = unlockPhase
              ? `Advance to ${PHASE_LABELS[unlockPhase]} phase to unlock`
              : 'Not available in this phase'

            return (
              <span
                key={tab.href}
                title={tooltip}
                className="flex items-center gap-1.5 whitespace-nowrap border-b-2 border-transparent px-3 py-2.5 text-sm font-medium text-muted-foreground/40 cursor-not-allowed select-none"
              >
                <Lock className="h-3.5 w-3.5" />
                {tab.label}
              </span>
            )
          }

          return (
            <Link
              key={tab.href}
              to={tab.href}
              className={cn(
                'flex items-center gap-1.5 whitespace-nowrap border-b-2 px-3 py-2.5 text-sm font-medium transition-colors',
                isActive(tab.href)
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:border-border hover:text-foreground'
              )}
            >
              {tab.icon}
              {tab.label}
            </Link>
          )
        })}
      </nav>
    </div>
  )
}
